/**
 * TranslationFeedbackModal — calm "Help us phrase this better" modal.
 *
 * Opens from a single footer link on non-English locales. Posts to
 * POST /api/translation-feedback. Hidden when locale is en-US (we don't
 * accept feedback on the source language).
 *
 * Submission is fire-and-forget from the user's POV — toast confirms,
 * modal closes. Errors land in a soft inline message, never alarming.
 */
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { LOCALES } from "../lib/locales-config";

export default function TranslationFeedbackModal({ open, onClose }) {
  const { t, locale } = useI18n();
  const [suggestion, setSuggestion] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) {
      setSuggestion("");
      setEmail("");
      setError(null);
      setSending(false);
      return;
    }
    // Remember what had focus before we opened, so we can restore on close.
    previouslyFocused.current = document.activeElement;
    // Move focus into the dialog (textarea is the first meaningful input).
    const id = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 30);
    return () => {
      window.clearTimeout(id);
      // Restore focus to the trigger when the modal closes.
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // ESC to close + focus trap (Tab cycles inside the dialog).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const localeLabel = LOCALES[locale]?.autonym || locale;
  const surface = typeof window !== "undefined" ? window.location.pathname : null;

  async function submit(e) {
    e.preventDefault();
    if (suggestion.trim().length < 4) {
      setError(t("marketing.translation_feedback.min_length"));
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.post("/translation-feedback", {
        locale,
        surface,
        source_key: null,
        suggestion: suggestion.trim(),
        reviewer_email: email.trim() || null,
      });
      toast.success(t("marketing.translation_feedback.thanks_toast"));
      onClose();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = t("errors.generic");
      if (typeof raw === "string" && raw) msg = raw;
      else if (Array.isArray(raw) && raw.length > 0) msg = raw[0]?.msg || msg;
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      data-testid="translation-feedback-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="translation-feedback-heading"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 bg-[#0F2018]/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_24px_48px_rgba(31,58,42,0.32)] border border-[#EEF0F4] p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label={t("marketing.translation_feedback.close_aria")}
          onClick={onClose}
          data-testid="translation-feedback-close"
          className="absolute top-4 right-4 text-[#6B7280] hover:text-[#1A1B2E] transition-colors p-1.5"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
          {t("marketing.translation_feedback.eyebrow", { locale: localeLabel })}
        </div>
        <h2
          id="translation-feedback-heading"
          className="mt-3 font-display text-2xl tracking-tight leading-snug text-[#1A1B2E]"
        >
          {t("marketing.translation_feedback.title")}
        </h2>
        <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">
          {t("marketing.translation_feedback.body")}
        </p>

        <form onSubmit={submit} data-testid="translation-feedback-form" className="mt-6 space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
              {t("marketing.translation_feedback.field_suggestion")}
            </span>
            <textarea
              ref={textareaRef}
              data-testid="translation-feedback-suggestion"
              required
              rows={4}
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              maxLength={2000}
              className="input-soft w-full"
              placeholder={t("marketing.translation_feedback.suggestion_placeholder")}
              dir="auto"
            />
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
              {t("marketing.translation_feedback.field_email_optional")}
            </span>
            <input
              data-testid="translation-feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-soft w-full"
              placeholder="you@quiet.place"
            />
          </label>

          {error && (
            <p
              data-testid="translation-feedback-error"
              role="alert"
              className="text-sm text-[#B91C1C] leading-relaxed"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              data-testid="translation-feedback-cancel"
              onClick={onClose}
              className="text-sm text-[#6B7280] hover:text-[#1A1B2E] px-4 py-2 transition-colors"
            >
              {t("marketing.translation_feedback.cancel")}
            </button>
            <button
              type="submit"
              data-testid="translation-feedback-submit"
              disabled={sending}
              className="btn-primary !py-2 !px-5 !text-sm"
            >
              {sending
                ? t("marketing.translation_feedback.submit_pending")
                : t("marketing.translation_feedback.submit")}
            </button>
          </div>
        </form>

        <p className="mt-5 text-[11px] text-[#9CA3AF] leading-relaxed">
          {t("marketing.translation_feedback.privacy_note")}
        </p>
      </div>
    </div>
  );
}
